'use client';

import {
	useEffectEvent,
	useEffect,
	useRef,
	useState,
	type MouseEventHandler,
} from 'react';
import { useTranslations, useLocale } from 'next-intl';
import cn from 'classnames';
import { Link } from 'src/navigation';
import { SearchItem } from 'components/global/global-navigation/search-item';
import { NavLink } from 'components/global/global-navigation/nav-link';
import { TicketLink } from 'components/homepage/ticket-link';
import NavItem from 'components/global/global-navigation/nav-item';
import SearchSection from 'components/global/global-navigation/search-section';
import LanguageFilter from 'components/controls/language-filter';
import MetLogo from 'assets/icons/met-logo.svg';
import useMediaQuery from 'hooks/use-media-query';
import { BREAKPOINT_M } from 'lib/constants.mjs';
import { getEngageBaseUrl } from 'lib/engage';
import type { GlobalNavigationCategory } from '../types';
import styles from './desktop-navbar.module.scss';

function DesktopNavbar({
	categories,
	navigationMakeADonationCode,
	navigationMembershipCode,
	navigationBuyTicketsCode,
	mode = 'inline',
}: GlobalNavigationProps) {
	const navRef = useRef(null);
	const { matches: isDesktopViewport } = useMediaQuery(
		`(min-width: ${BREAKPOINT_M}px)`,
	);
	const t = useTranslations('navigation');
	const locale = useLocale();
	const [areTransitionsSuspended, setAreTransitionsSuspended] = useState(false);
	const [openNavItem, setOpenNavItem] = useState<string | null>(null);
	const [showSearch, setShowSearch] = useState(false);
	const [prevIsDesktopViewport, setPrevIsDesktopViewport] =
		useState(isDesktopViewport);

	const isSubViewOpen = openNavItem !== null || showSearch;

	// The desktop navbar is hidden via CSS once the viewport grows smaller than the desktop
	// breakpoint, but its state otherwise persists. Close all menus so that they don't remain open
	// if the viewport grows back to desktop size after being shrunk down to mobile.
	if (isDesktopViewport !== prevIsDesktopViewport) {
		setPrevIsDesktopViewport(isDesktopViewport);

		if (!isDesktopViewport) {
			setOpenNavItem(null);
			setShowSearch(false);
		}
	}

	// Close all open menus when the user clicks or focuses outside of the navbar.
	// By scoping this to `page-container`, we avoid closing the menu when the user clicks
	// either the "Skip to main content" button or the global notification banner.
	const handleOutsideEvent = useEffectEvent((e: MouseEvent | FocusEvent) => {
		const pageContainer = document.getElementById('page-container');

		if (
			e.target instanceof Node &&
			pageContainer?.contains(e.target) &&
			!navRef?.current?.contains(e.target)
		) {
			if (openNavItem !== null || showSearch) {
				setAreTransitionsSuspended(true);
			}

			setOpenNavItem(null);
			setShowSearch(false);
		}
	});

	useEffect(() => {
		document.addEventListener('click', handleOutsideEvent);
		document.addEventListener('focusin', handleOutsideEvent);

		return () => {
			document.removeEventListener('click', handleOutsideEvent);
			document.removeEventListener('focusin', handleOutsideEvent);
		};
	}, []);

	// We disable transitions for just a couple of frames when we close submenus of the navbar
	// so that we don't have weird transitions at the tail end.
	useEffect(() => {
		if (!areTransitionsSuspended) {
			return;
		}

		let frameReqId = requestAnimationFrame(() => {
			frameReqId = requestAnimationFrame(() => {
				setAreTransitionsSuspended(false);
				frameReqId = null;
			});
		});

		return () => {
			if (frameReqId === null) {
				return;
			}

			cancelAnimationFrame(frameReqId);
		};
	}, [areTransitionsSuspended]);

	// Toggle search section
	const handleSearchClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		const nextShowSearch = !showSearch;

		if (openNavItem !== null || (showSearch && !nextShowSearch)) {
			setAreTransitionsSuspended(true);
		}

		setOpenNavItem(null);
		setShowSearch(nextShowSearch);
	};

	// Toggle Submenu.
	const handleNavItemClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		const navSectionID = e.currentTarget.dataset.navId;
		const nextOpenNavItem =
			openNavItem === navSectionID ? null : (navSectionID ?? null);

		if (openNavItem !== null && nextOpenNavItem === null) {
			setAreTransitionsSuspended(true);
		}

		setOpenNavItem(nextOpenNavItem);
	};

	return (
		<>
			{isSubViewOpen && (
				<div
					className={styles.navScrim}
					aria-hidden="true"
					data-testid="nav-scrim"
				/>
			)}
			<header
				className={cn(styles.desktopNavbarContainer, {
					[styles.overlay]: mode === 'overlay',
					[styles.subNavOpen]: isSubViewOpen,
					[styles.transitionsSuspended]: areTransitionsSuspended,
				})}
				ref={navRef}
				data-lock-page={isSubViewOpen}
				data-testid="global-navbar-desktop"
			>
				<div className={styles.desktopNavbar}>
					<Link
						title="Homepage"
						href="/"
						className={styles.logoLink}
						data-analytics-id="nav-logo-link"
					>
						<MetLogo data-testid="met-logo" aria-hidden="true" />
					</Link>
					<div className={styles.desktopNavContentWrapper}>
						<div className={styles.desktopNavBtns}>
							<LanguageFilter isNavTheme={true} />
							<NavLink
								text={t('donate')}
								href={`${getEngageBaseUrl(locale)}/donate?promocode=${navigationMakeADonationCode ?? ''}`}
								className={styles.navBtn}
								data-analytics-id="nav-donate-link"
							/>
							<NavLink
								text={t('membership')}
								href={`${getEngageBaseUrl(locale)}/members/membership/?promocode=${navigationMembershipCode ?? ''}`}
								className={styles.navBtn}
								data-analytics-id="nav-membership-link"
							/>
							<TicketLink
								variant="small"
								navigationBuyTicketsCode={navigationBuyTicketsCode}
								className={cn(styles.navBtn, styles.ticketLink)}
								data-analytics-id="nav-ticket-link"
							/>
						</div>
						<div className={styles.navLinksContainer}>
							<nav className={styles.navLinks}>
								<ul className={styles.navGroupLinks}>
									{categories?.map((category) => (
										<NavItem
											handleClick={handleNavItemClick}
											isOpen={category.title === openNavItem}
											key={category.title}
											url={category.url}
											links={category.links}
											orientingCopy={category.orientingCopy}
											title={category.title}
										/>
									))}
									<SearchItem handleClick={handleSearchClick} />
								</ul>
							</nav>
						</div>
					</div>
				</div>
				{showSearch && <SearchSection toggleVisible={setShowSearch} />}
			</header>
		</>
	);
}

export { DesktopNavbar };

export interface GlobalNavigationProps {
	categories: GlobalNavigationCategory[];
	navigationMakeADonationCode?: number;
	navigationMembershipCode?: number;
	navigationBuyTicketsCode?: number;
	mode?: 'overlay' | 'inline';
}
